import { format, isPast, isValid, parse } from 'date-fns';
import { currentLanguage } from 'i18n';

const localeToUse = {
  en: 'en-GB',
  de: 'de-DE',
};
const formatDateTime = (
  str: string | number | Date,
  options: Intl.DateTimeFormatOptions,
  locale = currentLanguage,
): string => {
  const date = new Date(str);

  if (isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat(localeToUse[locale], options).format(date);
};

export const formatDay = (str?: string, locale = currentLanguage): string => {
  if (!str) {
    return '';
  }
  return formatDateTime(str, { year: 'numeric', month: '2-digit', day: '2-digit' }, locale);
};

export const formatDayAndTime = (date: string | number | Date, locale = currentLanguage): string => {
  return formatDateTime(
    date,
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    },
    locale,
  );
};

export const formatTimestamp =
  <T>(formatFunction: (args: Date) => T) =>
  (timestamp?: number): T | undefined => {
    if (!timestamp) {
      return undefined;
    }

    const date = new Date(timestamp * 1000);
    if (!date || isNaN(date.getTime())) {
      return undefined;
    }

    return formatFunction(date);
  };

export const formatTimestampToDay = (timestamp?: number) => {
  const formatFunction = (date: Date) => formatDay(formatDateToISO(date));
  return formatTimestamp(formatFunction)(timestamp);
};

export const formatDateToISO = (date: Date): string => format(date, 'yyyy-MM-dd');

export const isDateInPast = (date: Date) => isPast(date);

//https://www.notion.so/procuros/Dates-Timestamps-Timezones-904a5168105c497ebef01d929b29309c?pvs=4
export const setToMidday = (date: string | number | Date): Date => {
  const newDate = new Date(date);
  return new Date(newDate.setHours(12));
};

export function diffInMinutes(date1: string, date2 = new Date(), absolute = true) {
  return Math.floor(diffInSeconds(date1, date2, absolute) / 60);
}

export function diffInSeconds(date1: string | number | Date, date2 = new Date(), absolute = true) {
  const diff = new Date(date1).getTime() - new Date(date2).getTime();
  const seconds = Math.floor(diff / 1000);

  return absolute ? Math.abs(seconds) : seconds;
}

export const fromStringDateToProcurosTimestamp = (date: string | number | Date): number =>
  setToMidday(date).getTime() / 1000;

export const formatISOForFilename = (date: Date = new Date()): string => {
  return date.toISOString().replace(/[:.]/g, '-');
};

export const parseFlexibleDate = (dateString: string) => {
  const formats = ['dd/MM/yyyy', 'yyyy/MM/dd', 'dd-MM-yyyy', 'yyyy-MM-dd', 'dd.MM.yyyy', 'yyyy.MM.dd'];

  for (const format of formats) {
    const date = parse(dateString, format, new Date());
    if (isValid(date)) {
      return date;
    }
  }

  throw new Error('Unable to parse date with any known format');
};

const DAYS_PER_YEAR = 365;
const DAYS_PER_MONTH = 30;
const DAYS_PER_WEEK = 7;

export const convertDaysToTimeUnit = (days: number): { quantity: number; unit: string } => {
  if (days >= DAYS_PER_YEAR) {
    return { quantity: Math.floor(days / DAYS_PER_YEAR), unit: 'year' };
  }
  if (days >= DAYS_PER_MONTH) {
    return { quantity: Math.floor(days / DAYS_PER_MONTH), unit: 'month' };
  }
  if (days >= DAYS_PER_WEEK) {
    return { quantity: Math.floor(days / DAYS_PER_WEEK), unit: 'week' };
  }
  return { quantity: Math.floor(days), unit: 'day' };
};
